import { useMemo, useState } from 'react'
import { LinkIcon, Loader2, Settings2Icon, TriangleAlert } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import Card from 'app/frontend/base/components/Card'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo, useRaidPlans } from 'app/frontend/base/config/queries'
import { Badge } from '@/components/ui/badge'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { cn } from '@/lib/utils'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import {
  clearActorOverrides,
  clearCastTargetOverrides,
  getEncounterAssignmentAbilityHidden,
  getPlanEncounterAssignmentOverrides,
  EventPlanOverridesByPlanId,
} from './eventActivityPlanOverrides'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import EventActivityBlock from './EventActivityBlock'
import EventActivityIssuesBadge from './EventActivityIssuesBadge'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import EventActivityHeaderContextMenu from './EventActivityHeaderContextMenu'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'
import RaidPlanCandidatePicker from '../../TeamPlans/components/RaidPlanCandidatePicker'
import { buildEventActivityLinkedPlanNote } from './eventActivityNoteExport'
import { resolveLinkedPlanTimelineMaxTime } from '../hooks/resolveLinkedPlanEncounterAssignmentRows'
import { Link } from 'react-router-dom'
import LinkButton from '@/components/ui/link-button'
import ResolvedPlanPreviewContent from './ResolvedPlanPreviewContent'

type Props = {
  activity: wowaudit.EventActivity
  activities: wowaudit.EventActivity[]
  allSelectedForActivity: boolean
  linkedPlanData?: EventActivityLinkedPlanData
  isLinkedPlanDataLoading: boolean
}

const EventActivityPlan = ({
  activity,
  activities,
  allSelectedForActivity,
  linkedPlanData,
  isLinkedPlanDataLoading,
}: Props) => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const canEdit = accessLevel === 'admin'
  const {
    data: { currentSeason, gameVersion },
  } = usePageInfo()
  const { updateEventActivity, exportEventActivityOverrides, isSaving } =
    useTeamEventUpdateContext()
  const { activityIssuesById, allEntries } = useTeamEventParticipantsContext()
  const { copied, copyToClipboard } = useCopyToClipboard()
  const [isPlanPopoverOpen, setIsPlanPopoverOpen] = useState(false)
  const { data: raidPlans } = useRaidPlans(
    { ownerType: 'team', ownerId: team.id },
    currentSeason?.id,
  )
  const linkedRaidPlan = linkedPlanData?.linkedRaidPlan
  const candidatePlans = useMemo(() => {
    const encounterId = activity.encounter?.id
    if (!encounterId) return []
    return (raidPlans ?? [])
      .filter((raidPlan) => raidPlan.encounterId === encounterId)
      .sort((left, right) => {
        if (left.difficulty !== right.difficulty) {
          if (left.difficulty === activity.difficulty) return -1
          if (right.difficulty === activity.difficulty) return 1
          return difficultiesLabels[left.difficulty].localeCompare(
            difficultiesLabels[right.difficulty],
          )
        }
        return left.name.localeCompare(right.name)
      })
  }, [activity.difficulty, activity.encounter?.id, raidPlans])
  const linkedPlanAssignments = linkedPlanData?.linkedPlanAssignments ?? []
  const selectedParticipants = linkedPlanData?.selectedParticipants ?? []
  const selectedRows = linkedPlanData?.selectedRows ?? []
  const resolvedActors = linkedPlanData?.resolvedActors ?? []
  const planOverrides =
    (activity.planOverrides as EventPlanOverridesByPlanId) ?? {}
  const hasPlanOverrides = useMemo(() => {
    if (!linkedRaidPlan) return false
    const currentPlanOverrides = planOverrides[linkedRaidPlan.id.toString()]
    if (!currentPlanOverrides) return false
    const actorEntries = currentPlanOverrides.actorOverrides?.actors ?? {}
    const abilityEntries =
      currentPlanOverrides.encounterAssignments?.abilities ?? {}
    return (
      Object.keys(actorEntries).length > 0 ||
      Object.keys(abilityEntries).length > 0
    )
  }, [linkedRaidPlan, planOverrides])
  const encounterAssignmentOverrides = getPlanEncounterAssignmentOverrides(
    planOverrides,
    linkedRaidPlan?.id,
  )
  const resolverResult = linkedPlanData?.resolverResult ?? {
    resolvedByScopeBoxKey: {},
    issues: [],
  }
  const linkedPlanIssueCount = linkedPlanData?.linkedPlanIssueCount ?? 0
  const hasLinkedPlanIssues = linkedPlanData?.hasLinkedPlanIssues ?? false
  const issues = activityIssuesById.get(activity.id)
  const selectedAbilityRows = linkedPlanAssignments
    .map((entry, index) => {
      const row = selectedRows[index]
      if (!row) return null
      const ability = entry.ability
      return {
        abilityKey: entry.abilityKey,
        row,
        ability,
        isHidden: getEncounterAssignmentAbilityHidden({
          encounterAssignments: encounterAssignmentOverrides,
          abilityKey: row.abilityKey,
        }),
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
  const sortedAbilityRows = [...selectedAbilityRows].sort(
    (left, right) => Number(left.isHidden) - Number(right.isHidden),
  )
  const cooldownMaxTime = useMemo(() => {
    if (linkedRaidPlan) {
      return resolveLinkedPlanTimelineMaxTime({
        raidPlan: linkedRaidPlan,
        timeline: linkedRaidPlan.timeline,
      })
    }
    const maxActorCastAt = resolvedActors.reduce((actorMax, resolvedActor) => {
      const actorCastMax = resolvedActor.actor.casts.reduce((castMax, cast) => {
        return Math.max(castMax, cast.at)
      }, 0)
      return Math.max(actorMax, actorCastMax)
    }, 0)
    const maxEncounterEnd = selectedRows.reduce((rowMax, row) => {
      const rowEnd = row.entries.reduce((entryMax, entry) => {
        return Math.max(entryMax, entry.time + entry.duration)
      }, 0)
      return Math.max(rowMax, rowEnd)
    }, 0)
    return Math.max(maxActorCastAt, maxEncounterEnd, 1)
  }, [linkedRaidPlan, resolvedActors, selectedRows])
  const copiedNote = copied !== undefined
  const handleCopyNote = () => {
    const note = buildEventActivityLinkedPlanNote({
      activity,
      linkedPlanData,
      allEntries,
    })
    if (!note) return
    copyToClipboard(note)
  }
  const handlePlanOverridesChange = (
    nextPlanOverrides: EventPlanOverridesByPlanId,
  ) => {
    void updateEventActivity(activity.id, {
      planOverrides: nextPlanOverrides,
    })
  }

  return (
    <Card className='mt-4'>
      <div className='flex items-center justify-between gap-2 mb-4'>
        <div className='flex items-center gap-3'>
          <img
            className={cn(
              'w-8 h-8 rounded-md border-2 border-border shrink-0 transition-colors',
              !activity.active && 'grayscale',
            )}
            src={
              activity.encounter?.thumbnailUrl ??
              activity.instance?.tileUrl ??
              ''
            }
          />
          <h3 className='m-0'>
            {activity.encounter?.name || activity.instance?.name}
          </h3>
          <Badge variant='outline'>
            {activity.active
              ? difficultiesLabels[activity.difficulty]
              : 'Inactive'}
          </Badge>
        </div>

        <div className='flex items-center gap-2'>
          <Button
            size='sm'
            variant={linkedRaidPlan ? 'default' : 'outline'}
            className='w-26'
            onClick={handleCopyNote}
            disabled={isSaving}
          >
            {copiedNote
              ? 'Copied!'
              : linkedRaidPlan
              ? 'Copy note'
              : 'Copy invites'}
          </Button>

          {canEdit ? (
            <>
              {activity.encounter && gameVersion === 'live' ? (
                <Popover
                  open={isPlanPopoverOpen}
                  onOpenChange={setIsPlanPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size='sm'
                      disabled={isSaving}
                      className={cn(
                        'transition-opacity max-w-64',
                        activity.raidPlanId && !linkedRaidPlan
                          ? 'opacity-0'
                          : 'opacity-100',
                      )}
                      variant={linkedRaidPlan ? 'outline' : 'default'}
                    >
                      {linkedRaidPlan ? (
                        <span className='flex min-w-0 items-center gap-2'>
                          <LinkIcon className='h-4 w-4 shrink-0 text-success-foreground' />
                          <span className='truncate'>
                            {linkedRaidPlan.name}
                          </span>
                        </span>
                      ) : (
                        'Link raid plan'
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-fit'>
                    <div className='space-y-2'>
                      <RaidPlanCandidatePicker
                        plans={candidatePlans}
                        selectedPlanId={activity.raidPlanId}
                        warningDifficulty={activity.difficulty}
                        teamPath={team.path}
                        emptyActionPath={`${team.path}/plans`}
                        onSelect={(plan) =>
                          void updateEventActivity(activity.id, {
                            raidPlanId: plan.id,
                          }).finally(() => setIsPlanPopoverOpen(false))
                        }
                      />
                      {activity.raidPlanId ? (
                        <Button
                          variant='outline'
                          size='sm'
                          className='w-full'
                          onClick={() =>
                            void updateEventActivity(activity.id, {
                              raidPlanId: null,
                            })
                          }
                        >
                          Unlink
                        </Button>
                      ) : null}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : null}

              <EventActivityHeaderContextMenu
                activity={activity}
                activities={activities}
                allSelected={allSelectedForActivity}
                extraItems={
                  linkedRaidPlan && hasPlanOverrides ? (
                    <DropdownMenuItem
                      disabled={isSaving}
                      onSelect={(event) => {
                        event.preventDefault()
                        if (isSaving) return
                        void exportEventActivityOverrides(activity.id)
                      }}
                    >
                      <span className='flex items-center gap-2'>
                        Save changes to new plan
                      </span>
                    </DropdownMenuItem>
                  ) : null
                }
                trigger={
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    aria-label='Activity settings'
                  >
                    <Settings2Icon className='h-4 w-4' />
                  </Button>
                }
              />
            </>
          ) : null}
        </div>
      </div>

      {activity.encounter ? (
        <>
          {!activity.raidPlanId ? (
            <p className='text-sm text-muted-foreground text-center mt-6 mb-8'>
              No raid plan has been linked to this activity yet
            </p>
          ) : isLinkedPlanDataLoading || !linkedRaidPlan ? (
            <p className='text-muted-foreground flex justify-center text-center mt-6 mb-8'>
              <Loader2 className='h-8 w-8 animate-spin' />
            </p>
          ) : (
            <>
              {linkedPlanIssueCount > 0 ? (
                <Alert variant='warning' className='pb-2 mb-4'>
                  <TriangleAlert className='h-4 w-4' />
                  <AlertTitle>
                    Issues to resolve: {linkedPlanIssueCount}
                  </AlertTitle>
                  <AlertDescription>
                    You can resolve issues directly in this event by clicking on
                    them. You can also edit the{' '}
                    <Link
                      className='text-primary hover:text-primary/90'
                      to={`${team.path}/plans/${linkedRaidPlan.id}`}
                    >
                      base plan
                    </Link>
                    , which will affect all events that use it.
                  </AlertDescription>
                </Alert>
              ) : null}

              {selectedRows.length === 0 && resolvedActors.length === 0 ? (
                <p className='text-muted-foreground text-center mt-6 mb-8'>
                  The linked raid plan for this encounter is empty
                </p>
              ) : null}
              <ResolvedPlanPreviewContent
                raidPlan={linkedRaidPlan}
                planListPathPrefix={
                  linkedRaidPlan.ownerType === 'User' ? '/profile' : team.path
                }
                rosterMemberships={team.members ?? []}
                planOverrides={planOverrides}
                onPlanOverridesChange={handlePlanOverridesChange}
                sortedAbilityRows={sortedAbilityRows}
                selectedRows={selectedRows}
                phaseDividers={linkedPlanData?.phaseDividers ?? []}
                selectedParticipants={selectedParticipants}
                resolvedByScopeBoxKey={resolverResult.resolvedByScopeBoxKey}
                resolvedActors={resolvedActors}
                resolvedCastTargetsByCastId={
                  linkedPlanData?.castTargetResolverResult.resolvedByCastId ??
                  {}
                }
                cooldownMaxTime={cooldownMaxTime}
                canEdit={canEdit}
                isSaving={isSaving}
                assignmentHeaderActions={
                  canEdit ? (
                    <LinkButton
                      size='xs'
                      variant='default'
                      to={`${team.path}/plans/${linkedRaidPlan.id}#assignments`}
                    >
                      Edit base plan
                    </LinkButton>
                  ) : null
                }
                cooldownHeaderActions={
                  canEdit ? (
                    <div className='flex items-center gap-2'>
                      <LinkButton
                        size='xs'
                        variant='default'
                        to={`${team.path}/plans/${linkedRaidPlan.id}#key_cooldown`}
                      >
                        Edit base plan
                      </LinkButton>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='outline'
                            size='xs'
                            data-testid='cooldown-settings-button'
                          >
                            <Settings2Icon className='h-3.5 w-3.5' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem
                            onSelect={() => {
                              const actorResetOverrides = clearActorOverrides({
                                planOverrides,
                                planId: linkedRaidPlan.id,
                              })
                              const nextPlanOverrides =
                                clearCastTargetOverrides({
                                  planOverrides: actorResetOverrides,
                                  planId: linkedRaidPlan.id,
                                })
                              handlePlanOverridesChange(nextPlanOverrides)
                            }}
                          >
                            Reset changes
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : null
                }
              />
            </>
          )}
        </>
      ) : null}

      <div className='flex items-center justify-between gap-2 mt-6'>
        <h4>Setup</h4>
        {activity.active ? (
          <div className='h-5 flex items-center justify-between gap-2'>
            <span className='text-sm font-mono flex items-center gap-0.5'>
              <span
                className={cn(
                  issues?.tooManyPlayers
                    ? 'text-warning-foreground'
                    : issues?.selectedCount === activity.raidSize
                    ? 'text-success-foreground'
                    : 'text-foreground',
                )}
              >
                {issues?.selectedCount ?? 0}
              </span>
              <span className='text-muted-foreground'>/</span>
              <span>{activity.raidSize ?? '-'}</span>
            </span>
            <EventActivityIssuesBadge
              activityId={activity.id}
              hasLinkedPlanIssues={hasLinkedPlanIssues}
              linkedPlanIssueCount={linkedPlanIssueCount}
            />
          </div>
        ) : (
          <div className='text-xs text-muted-foreground h-5'>inactive</div>
        )}
      </div>

      <EventActivityBlock activity={activity} />
    </Card>
  )
}

export default EventActivityPlan
